<template>
  <div id="devicePosition" style="width: 100%; height: 100%; overflow: hidden;">
    <el-container v-loading="loading" style="width: 100%; height: 100%;" element-loading-text="加载中">
      <el-container style="width: 100%; height: 100%;">
        <!-- 顶部工具栏 -->
        <el-header height="48px" style="background: #1a1e26; display: flex; align-items: center; justify-content: space-between; padding: 0 20px; border-bottom: 1px solid #2a2e36;">
          <div style="display: flex; align-items: center; gap: 12px;">
            <span style="color: #e0e0e0; font-size: 14px; font-weight: 600; padding-left: 10px">分屏监控</span>
            <span style="color: #909399; font-size: 12px;">
              共 <b style="color: #409eff;">{{ total }}</b> 个设备，
              在线 <b style="color: #67c23a;">{{ onlineCount }}</b> 个
            </span>
          </div>
          <div style="display: flex; align-items: center; gap: 8px; padding-right: 10px">
            <el-button icon="el-icon-refresh-right" circle size="mini" :loading="refreshing" @click="refreshAll" title="刷新列表"></el-button>
          </div>
        </el-header>

        <!-- 宫格区域 -->
        <el-main style="padding: 8px 20px; background: #0a0e14; overflow: hidden;">
          <div class="grid-container" @scroll="handleScroll">
            <div
              v-for="(dev, vidx) in devices"
              :key="dev.deviceId"
              class="grid-cell"
              :class="{
                'cell-active': dev.deviceId === playingDeviceId,
                'cell-offline': !dev.online
              }">

              <!-- 左上角设备名称 -->
              <div class="cell-label">
                <span class="label-text">{{ dev.deviceName }}</span>
                <span class="label-dot" :class="dev.online ? 'dot-online' : 'dot-offline'"></span>
                <span class="label-status-text">{{ dev.online ? '在线' : '离线' }}</span>
              </div>

              <!-- 多通道编号按钮（右上角） -->
              <div v-if="dev.channels && dev.channels.length > 1" class="channel-btns">
                <div
                  v-for="(ch, ci) in dev.channels"
                  :key="ch.channelId"
                  class="channel-btn"
                  :class="{ 'ch-active': dev.playingChannelIdx === ci }"
                  :title="ch.channelName || ('通道' + (ci + 1))"
                  @click.stop="selectChannel(dev, ci)">

                  {{ ci + 1 }}
                </div>
              </div>

              <!-- 状态：1个通道/未播放 — 播放按钮 -->
              <div v-if="dev.playingChannelIdx < 0 && dev.channels && dev.channels.length >= 1" class="cell-placeholder">
                <div class="play-overlay" @click.stop="startPlayDevice(dev, 0)">
                  <div class="play-ring">
                    <i class="el-icon-caret-right"></i>
                  </div>
                </div>
                <!-- 单通道时显示通道名 -->
                <div v-if="dev.channels.length === 1" class="single-channel-name">
                  {{ dev.channels[0].channelName || '通道1' }}
                </div>
              </div>

              <!-- 状态：无通道 -->
              <div v-else-if="!dev.channels || dev.channels.length === 0" class="cell-placeholder">
                <div class="no-channel-hint">
                  <i class="el-icon-close" style="font-size: 28px; opacity: 0.25;"></i>
                  <span>无通道</span>
                </div>
              </div>

              <!-- 状态：正在播放 -->
              <div v-else class="cell-player">
                <player
                  ref="player"
                  :videoUrl="dev.videoUrl"
                  :deviceId="dev.deviceId"
                  :channelId="dev.channels[dev.playingChannelIdx] ? dev.channels[dev.playingChannelIdx].channelId : ''"
                  fluent
                  @destroy="onPlayerDestroy(dev.deviceId)"
                  @screenshot="shot" />
              </div>

              <!-- 加载遮罩 -->
              <div v-if="dev.loading" class="cell-loading">
                <i class="el-icon-loading"></i>
                <span>正在拉流...</span>
              </div>
            </div>

            <!-- 加载更多 -->
            <div v-if="hasMore" class="load-more-hint" style="grid-column: 1 / -1;">
              <i class="el-icon-loading" v-if="pageLoading"></i>
              <span>{{ pageLoading ? '加载中...' : '下拉加载更多' }}</span>
            </div>

            <!-- 空状态 -->
            <div v-if="!loading && devices.length === 0" class="empty-all">
              <i class="el-icon-video-camera" style="font-size: 48px; opacity: 0.3;"></i>
              <span style="margin-top: 12px; color: #4a4f58;">暂无设备</span>
            </div>
          </div>
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script>
import player from './common/jessibuca.vue'
import DeviceService from './service/DeviceService.js'

export default {
  name: 'live',
  components: { player },
  data() {
    return {
      devices: [],
      playingDeviceId: null,
      loading: false,
      refreshing: false,
      currentPage: 0,
      total: 0,
      onlineCount: 0,
      pageLoading: false
    }
  },
  computed: {
    hasMore() {
      return this.devices.length < this.total
    },
  },
  mounted() {
    this.loadFirstPage()
    this.loadResourceInfo()
  },
  methods: {
    loadResourceInfo() {
      this.$axios({
        method: 'get',
        url: '/api/server/resource/info'
      }).then(res => {
        if (res.data && res.data.code === 0 && res.data.data && res.data.data.device) {
          this.total = res.data.data.device.total || 0
          this.onlineCount = res.data.data.device.online || 0
        }
      }).catch(() => {})
    },
    loadFirstPage() {
      this.loading = true
      this.currentPage = 1
      this.devices = []
      const deviceService = new DeviceService()

      deviceService.getDeviceList(1, 12, (data) => {
        if (data.code === 0 && data.data && data.data.list) {
          data.data.list.forEach(device => {
            this.devices.push({
              deviceId: device.deviceId,
              deviceName: device.name || device.deviceId,
              online: device.onLine,
              channels: (device.channels || []).map(ch => ({
                channelId: ch.channelId,
                channelName: ch.name || ''
              })),
              playingChannelIdx: -1,
              videoUrl: null,
              loading: false
            })
          })
        }
        this.loading = false
      }, () => {
        this.loading = false
        this.$message.error('加载设备列表失败')
      })
    },

    loadNextPage() {
      if (this.pageLoading || !this.hasMore) return
      this.pageLoading = true
      const nextPage = this.currentPage + 1
      const deviceService = new DeviceService()

      deviceService.getDeviceList(nextPage, 12, (data) => {
        if (data.code === 0 && data.data && data.data.list) {
          data.data.list.forEach(device => {
            this.devices.push({
              deviceId: device.deviceId,
              deviceName: device.name || device.deviceId,
              online: device.onLine,
              channels: (device.channels || []).map(ch => ({
                channelId: ch.channelId,
                channelName: ch.name || ''
              })),
              playingChannelIdx: -1,
              videoUrl: null,
              loading: false
            })
          })
          this.currentPage = nextPage
        }
        this.pageLoading = false
      }, () => {
        this.pageLoading = false
      })
    },

    refreshAll() {
      this.refreshing = true
      this.devices.forEach(d => {
        d.playingChannelIdx = -1
        d.videoUrl = null
      })
      this.devices = []
      this.currentPage = 0
      this.playingDeviceId = null
      this.loadFirstPage()
      this.loadResourceInfo()
      setTimeout(() => { this.refreshing = false }, 500)
    },

    selectChannel(dev, chIdx) {
      this.playingDeviceId = dev.deviceId
      this.startPlayDevice(dev, chIdx)
    },

    startPlayDevice(dev, chIdx) {
      if (!dev || !dev.channels || !dev.channels[chIdx]) return

      this.playingDeviceId = dev.deviceId
      dev.loading = true
      // 找到在 devices 中的真实索引
      const realIdx = this.devices.findIndex(d => d.deviceId === dev.deviceId)
      if (realIdx >= 0) this.$set(this.devices, realIdx, { ...dev })

      const channelId = dev.channels[chIdx].channelId

      this.$axios({
        method: 'get',
        url: `/api/play/start/${dev.deviceId}/${channelId}`
      }).then(res => {
        if (res.data.code === 0 && res.data.data) {
          const videoUrl = location.protocol === 'https:'
            ? res.data.data.wss_flv
            : res.data.data.ws_flv
          dev.videoUrl = videoUrl
          dev.playingChannelIdx = chIdx
        } else {
          this.$message.error(res.data.msg || '拉流失败')
        }
      }).catch(() => {
        this.$message.error('拉流请求失败')
      }).finally(() => {
        dev.loading = false
        const realIdx = this.devices.findIndex(d => d.deviceId === dev.deviceId)
        if (realIdx >= 0) this.$set(this.devices, realIdx, { ...dev })
      })
    },

    onPlayerDestroy(deviceId) {
      const idx = this.devices.findIndex(d => d.deviceId === deviceId)
      if (idx < 0) return
      const dev = this.devices[idx]
      dev.playingChannelIdx = -1
      dev.videoUrl = null
      this.playingDeviceId = null
      this.$set(this.devices, idx, { ...dev })
    },

    handleScroll(e) {
      if (this.pageLoading || !this.hasMore) return
      const el = e.target
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120
      if (nearBottom) {
        this.loadNextPage()
      }
    },

    shot(e) {
      const base64ToBlob = function (code) {
        let parts = code.split(';base64,')
        let contentType = parts[0].split(':')[1]
        let raw = window.atob(parts[1])
        let rawLength = raw.length
        let uInt8Array = new Uint8Array(rawLength)
        for (let i = 0; i < rawLength; ++i) {
          uInt8Array[i] = raw.charCodeAt(i)
        }
        return new Blob([uInt8Array], { type: contentType })
      }
      let aLink = document.createElement('a')
      let blob = base64ToBlob(e)
      let evt = document.createEvent('HTMLEvents')
      evt.initEvent('click', true, true)
      aLink.download = '截图'
      aLink.href = URL.createObjectURL(blob)
      aLink.click()
    }
  }
}
</script>

<style scoped>
/* 宫格容器 */
.grid-container {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: auto;
  align-content: start;
  gap: 8px;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
}

/* 单个宫格 */
.grid-cell {
  position: relative;
  background: #14181f;
  border: 1px solid #2a2e36;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  height: 0;
  padding-bottom: 75%;
}

.grid-cell:hover {
  border-color: #409eff40;
}

.grid-cell.cell-active {
  border: 2px solid #409eff;
  box-shadow: 0 0 12px rgba(64, 158, 255, 0.25);
}

.grid-cell.cell-offline {
  background: #0d1117;
  border-color: #1e2228;
  opacity: 0.6;
}

/* ===== 左上角设备名称 ===== */
.cell-label {
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
  padding: 3px 8px;
  border-radius: 3px;
  max-width: calc(100% - 50px);
  white-space: nowrap;
  overflow: hidden;
  pointer-events: auto;
}

.label-text {
  font-size: 12px;
  font-weight: 500;
  color: #e0e0e0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.label-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-online { background: #67c23a; }
.dot-offline { background: #909399; }

.label-status-text {
  font-size: 10px;
  color: #909399;
  flex-shrink: 0;
}

/* ===== 右上角多通道编号按钮 ===== */
.channel-btns {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 20;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: flex-end;
  max-width: calc(100% - 120px);
  pointer-events: auto;
}

.channel-btn {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: rgba(64, 158, 255, 0.2);
  border: 1px solid rgba(64, 158, 255, 0.4);
  color: #409eff;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  line-height: 1;
}

.channel-btn:hover {
  background: rgba(64, 158, 255, 0.45);
  border-color: #409eff;
  color: #fff;
  transform: scale(1.1);
}

.channel-btn.ch-active {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
  box-shadow: 0 0 6px rgba(64, 158, 255, 0.5);
}

/* ===== 待播放占位 ===== */
.cell-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #14181f;
}

.play-overlay {
  cursor: pointer;
  transition: transform 0.15s;
}

.play-overlay:hover {
  transform: scale(1.08);
}

.play-ring {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(64, 158, 255, 0.16);
  border: 2px solid rgba(64, 158, 255, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, border-color 0.2s;
}

.play-ring i {
  font-size: 22px;
  color: #409eff;
  margin-left: 2px;
}

.play-overlay:hover .play-ring {
  background: rgba(64, 158, 255, 0.28);
  border-color: #409eff;
}

.single-channel-name {
  font-size: 11px;
  color: #6a6e76;
  position: absolute;
  bottom: 12px;
  text-align: center;
  width: 100%;
  padding: 0 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 无通道 */
.no-channel-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #3a3e46;
  font-size: 12px;
}

/* ===== 播放区域 ===== */
.cell-player {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: #000;
}

/* ===== 加载遮罩 ===== */
.cell-loading {
  position: absolute;
  inset: 0;
  z-index: 15;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #fff;
  font-size: 13px;
}

.cell-loading i {
  font-size: 24px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ===== 空状态 ===== */
.empty-all {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #4a4f58;
  font-size: 14px;
  grid-column: 1 / -1;
}

/* ===== 加载更多 ===== */
.load-more-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 0;
  color: #6a6e76;
  font-size: 13px;
  min-height: 48px;
}
</style>



// WEBPACK FOOTER //
// src/components/live.vue